/**
 * The chat and broadcast client — conversations, messages, read markers, and
 * composing to a filtered audience (spec §E8, §E9).
 *
 * A client per module, not a growing `lib/api.ts` (waves.md §1.1).
 *
 * **The staff surfaces only.** The portal's are Wave 5C's, on their own routes,
 * and nothing here is reachable by a client.
 *
 * ## `is_internal` is the server's fact, and the screen renders it loudly
 *
 * The rule that an internal note never reaches a client is held in the service —
 * a CHECK constraint, a read default, and a repository the portal cannot widen
 * (`repos/chat.py`). This file re-implements none of it. What it does do is make
 * the distinction **impossible to miss on screen**: `noteStyle()` exists because
 * a note that looks like a message is a note somebody writes thinking the client
 * will see it, and then writes the next one thinking they will not.
 */

import { api } from '../api'

// -- shapes ------------------------------------------------------------------

export type ConversationKind = 'case' | 'client'
export type MessageKind = 'message' | 'note' | 'system'
export type AuthorKind = 'user' | 'person' | 'system'
export type DeliveryStatus = 'pending' | 'posted' | 'sent' | 'skipped' | 'failed'
export type BroadcastStatus = 'draft' | 'sending' | 'sent' | 'failed'

export interface Conversation {
  id: string
  client_id: string
  case_id: string | null
  kind: ConversationKind
  subject: string | null
  last_message_at: string | null
  /** Computed by the service per reader, never stored. `null` when the caller
   *  did not ask for one. */
  unread: number | null
}

export interface ChatMessage {
  id: string
  conversation_id: string
  kind: MessageKind
  /** FR-E8's internal note. The **server's** answer, not a client-side guess. */
  is_internal: boolean
  body: string
  author_kind: AuthorKind
  author_user_id: string | null
  author_person_id: string | null
  /** Set when this message *is* a broadcast landing here — so the thread reads
   *  "sent to 240 clients" rather than as something typed to this one client. */
  broadcast_id: string | null
  attachment_ids: string[]
  at: string | null
}

export interface Thread {
  conversation: Conversation
  messages: ChatMessage[]
}

export interface AudienceFilters {
  service_id?: string
  category?: string
  stage_key?: string
  branch_id?: string
  client_kind?: string
  tag?: string
}

export interface AudienceMember {
  client_id: string
  name: string
  case_id: string | null
  case_number: string | null
  /** So the firm fixes a missing email **before** sending rather than reading it
   *  in a report afterwards. */
  has_address: boolean
}

export interface AudiencePreview {
  count: number
  audience: AudienceFilters
  recipients: AudienceMember[]
  truncated: boolean
}

export interface Broadcast {
  id: string
  subject: string
  body: string
  template_code: string | null
  channel: string | null
  /** The **filter** the firm chose, not a frozen list of client ids. */
  audience: AudienceFilters
  status: BroadcastStatus
  recipient_count: number
  posted_count: number
  sent_count: number
  failed_count: number
  sent_at: string | null
  created_at: string | null
  actor_user_id: string | null
}

export interface BroadcastRecipient {
  id: string
  client_id: string
  case_id: string | null
  conversation_id: string | null
  message_id: string | null
  status: DeliveryStatus
  channel: string | null
  reason: string | null
  sent_at: string | null
}

export interface BroadcastDetail {
  broadcast: Broadcast
  recipients: BroadcastRecipient[]
}

export interface BroadcastVocabulary {
  audience_filters: string[]
  channels: string[]
  max_audience: number
  can_send: boolean
}

// -- the client --------------------------------------------------------------

function query(filters: Record<string, unknown>): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value))
  }
  const text = params.toString()
  return text ? `?${text}` : ''
}

export const chat = {
  conversations: (filters: { client_id?: string; case_id?: string; kind?: ConversationKind } = {}) =>
    api<Conversation[]>(`/api/v1/chat/conversations${query(filters)}`),

  /** Find or open. Idempotent — the thread is the case's, not this request's. */
  open: (body: { case_id?: string; client_id?: string }) =>
    api<Conversation>('/api/v1/chat/conversations', { method: 'POST', body }),

  messages: (conversationId: string, before?: string) =>
    api<Thread>(
      `/api/v1/chat/conversations/${conversationId}/messages${query({ before })}`,
    ),

  post: (
    conversationId: string,
    body: { body: string; is_internal?: boolean; attachment_ids?: string[] },
  ) =>
    api<ChatMessage>(`/api/v1/chat/conversations/${conversationId}/messages`, {
      method: 'POST',
      body,
    }),

  markRead: (conversationId: string, lastMessageId?: string) =>
    api<{ conversation_id: string; last_read_at: string | null }>(
      `/api/v1/chat/conversations/${conversationId}/read`,
      { method: 'POST', body: { last_message_id: lastMessageId } },
    ),
}

export const broadcasts = {
  vocabulary: () => api<BroadcastVocabulary>('/api/v1/broadcasts/vocabulary'),

  /** **Always before sending.** A broadcast cannot be recalled, and the count is
   *  the confirmation dialog. */
  audience: (audience: AudienceFilters) =>
    api<AudiencePreview>('/api/v1/broadcasts/audience', { method: 'POST', body: { audience } }),

  list: () => api<Broadcast[]>('/api/v1/broadcasts'),

  read: (id: string) => api<BroadcastDetail>(`/api/v1/broadcasts/${id}`),

  send: (body: {
    subject: string
    body: string
    audience: AudienceFilters
    template_code?: string
    channel?: string
  }) =>
    api<{
      broadcast: Broadcast
      posted: number
      sent: number
      skipped: number
      failed: number
    }>('/api/v1/broadcasts', { method: 'POST', body }),
}

// -- pure helpers, so the screens' rules are testable ------------------------

export const DELIVERY_LABELS: Record<DeliveryStatus, string> = {
  pending: 'Queued',
  // Two different things, and collapsing them would hide the common case.
  posted: 'In their portal',
  sent: 'Sent',
  skipped: 'No address on file',
  failed: 'Failed',
}

/**
 * How a message renders, and the one place the internal/external distinction is
 * made visible.
 *
 * A note that looks like a message is a note somebody writes thinking the client
 * will see it — and then writes the next one thinking they will not. The label
 * is not decoration.
 */
export function noteStyle(message: Pick<ChatMessage, 'is_internal' | 'kind'>): {
  label: string | null
  tone: 'internal' | 'system' | 'normal'
} {
  if (message.is_internal) return { label: 'Internal — the client cannot see this', tone: 'internal' }
  if (message.kind === 'system') return { label: null, tone: 'system' }
  return { label: null, tone: 'normal' }
}

/**
 * The sentence under a "send" button, from a preview.
 *
 * It names the number **and** what will happen to the ones with no address,
 * because "240 clients" and "31 of them will only see it in the portal" are two
 * different pieces of information and the second is the one a firm acts on.
 */
export function audienceSummary(
  preview: AudiencePreview | null,
  withChannel: boolean,
): string {
  if (!preview) return ''
  if (preview.count === 0) return 'Nobody matches this audience yet.'
  const noun = preview.count === 1 ? 'client' : 'clients'
  if (!withChannel) {
    return `${preview.count} ${noun}. They will see this in their portal.`
  }
  const missing = preview.recipients.filter((r) => !r.has_address).length
  if (missing === 0) return `${preview.count} ${noun}, all of whom have an address on file.`
  return `${preview.count} ${noun}. ${missing} have no address on file and will only see this in their portal.`
}

/**
 * The four counts as one line, in the order a firm reads them.
 *
 * `posted` is deliberately first and is deliberately not called "failed": the
 * client has the message where they read things, and a firm told otherwise
 * re-sends something its clients already have.
 */
export function deliveryBreakdown(row: Broadcast): string {
  const parts: string[] = []
  if (row.sent_count) parts.push(`${row.sent_count} sent`)
  if (row.posted_count) parts.push(`${row.posted_count} in the portal`)
  const skipped = row.recipient_count - row.sent_count - row.posted_count
  if (skipped > 0) parts.push(`${skipped} with no address`)
  return parts.join(' · ') || 'Nothing sent yet'
}

/** Whether the compose form may be submitted. Cheap, local, and *not* the
 *  authority — the server refuses an empty audience too. */
export function canSend(
  draft: { subject: string; body: string },
  preview: AudiencePreview | null,
): boolean {
  return Boolean(draft.subject.trim() && draft.body.trim() && preview && preview.count > 0)
}
